import { Grid, Typography, capitalize } from "@mui/material";

import { FairPaySurveyResponse } from "features/FairPaySurvey/schemas";
import { formatLabel } from "utils/string";

const styles = {
  badge: {
    my: 2,
    background: "#B1B3F8",
    p: 2,
    borderRadius: "20px",
    border: `1px solid #DFDFDF`,
  },
  chip: {
    py: 1,
    px: 2,
    mb: 1,
    fontWeight: 700,
    fontSize: "18px",
    color: "#151F16",
    borderRadius: "20px",
    border: `1px solid rgba(21, 31, 22, 0.2)`,
  },
  descriptionLargeText: {
    textAlign: "center",
    fontSize: "28px",
    fontWeight: 600,
  },
  circle: {
    height: "30px",
    width: "30px",
    background: "#151F16",
    opacity: "10%",
    borderRadius: "15px",
  },
  circleFilled: {
    background: "#F7EE6F",
    opacity: "100%",
  },
  circleBlack: {
    opacity: "100%",
  },
  descriptionText: { textAlign: "center", maxWidth: "80%" },
};

export default function NichePercentile({
  surveyResponse,
}: {
  surveyResponse: FairPaySurveyResponse | null;
}) {
  if (!surveyResponse) {
    return null;
  }

  if (!surveyResponse?.nichePercentile) {
    return null;
  }

  const topPercentage = 100 - Math.round(surveyResponse.nichePercentile) || 1;

  const getNicheName = () => {
    let nicheResult = surveyResponse.niche;
    if (!nicheResult) {
      return "";
    }

    if (nicheResult === "other") {
      return "Your";
    }

    nicheResult = nicheResult.replace("/", " / ");

    return capitalize(nicheResult);
  };

  function isFilled(
    y: number,
    x: number,
    percentage: number,
    rows: number = 4,
    cols: number = 7,
  ) {
    const total = rows * cols;
    let target = Math.round((percentage / 100) * total);
    if (target === 0) {
      target = 1;
    }
    const coords = [];
    for (let yy = 0; yy < rows; yy++) {
      for (let xx = 0; xx < cols; xx++) {
        const distance = xx + yy;
        coords.push({ x: xx, y: yy, distance });
      }
    }
    coords.sort((a, b) =>
      a.distance === b.distance
        ? a.y - b.y || a.x - b.x
        : a.distance - b.distance,
    );
    const filled = coords.slice(0, target);
    return filled.some((c) => c.x === x && c.y === y);
  }

  function isBlack(
    y: number,
    x: number,
    percentage: number,
    rows: number = 4,
    cols: number = 7,
  ) {
    const total = rows * cols;
    const filledPercentage =
      percentage <= 25 ? 58 : percentage <= 50 ? 85 : 100;
    const filledCount = Math.round((filledPercentage / 100) * total);
    const index = x * rows + y;
    return index < filledCount;
  }

  return (
    <Grid item xs={12} justifyContent="center" container sx={styles.badge}>
      <Typography component="div" sx={styles.chip}>
        {formatLabel(getNicheName())} Niche
      </Typography>
      <Grid item xs={12} justifyContent="center" container>
        <Typography sx={styles.descriptionLargeText} gutterBottom>
          {topPercentage <= 10
            ? "Elite Earner"
            : topPercentage <= 25
              ? "Professional Earner"
              : topPercentage <= 50
                ? "Above Average"
                : "Emerging Earner"}{" "}
          <br />
          (Top {topPercentage}%)
        </Typography>
      </Grid>
      <Grid item xs={12} justifyContent="center" container>
        <Typography sx={styles.descriptionText}>
          In the {surveyResponse.niche} niche, you're in the top {topPercentage}
          % of earners!
        </Typography>
      </Grid>
      <Grid item xs={12} container gap={1} sx={{ my: 2 }}>
        {Array.from({ length: 4 }, (_, x) => (
          <Grid item key={x} container justifyContent="space-around" xs={12}>
            {Array.from({ length: 7 }, (_, y) => (
              <Grid
                item
                key={y}
                sx={[
                  styles.circle,
                  isFilled(x, y, topPercentage) && styles.circleFilled,
                  isBlack(x, y, topPercentage) && styles.circleBlack,
                ]}
              />
            ))}
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
}
